import React, { useContext, useEffect, useState } from "react";
import CalendarModal from "./CalendarModal";
import CreateEventButton from "./CreateEventButton";
import SmallCalendar from "./SmallCalendar";
import Labels from "./Labels";
import CalendarsService from "../services/calendars-service";
import UsersService from "../services/users-service";
import { AuthContext } from "react-oauth2-code-pkce";
import GlobalContext from "../context/GlobalContext";
import { IconEdit, IconTrash } from "./Icons";

export default function Sidebar({ onClose }) {
  const { token } = useContext(AuthContext);
  const { activeCalendarId, setActiveCalendarId, isReadonly } = useContext(GlobalContext);
  const [calendars, setCalendars] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [editCalendar, setEditCalendar] = useState(null); // calendar object for editing
  const [modalLoading, setModalLoading] = useState(false);
  const [modalError, setModalError] = useState(null);
  const [userCompanies, setUserCompanies] = useState([]);

  useEffect(() => {
    if (!token) return;
    setLoading(true);
    CalendarsService.fetchCalendars(token)
      .then((cals) => {
        setCalendars(cals);
        if (!activeCalendarId && cals.length > 0) {
          setActiveCalendarId(cals[0].id);
        }
      })
      .catch(() => setError("Kan kalenders niet laden."))
      .finally(() => setLoading(false));
  }, [token]);

  // Fetch user companies for calendar creation
  useEffect(() => {
    if (!token) return;
    UsersService.getMe(token)
      .then((userData) => {
        console.log('User data from /me:', userData); // Debug log
        // Extract companies from the user data - companies are directly on the user object
        const companies = userData.companies || [];
        console.log('Extracted companies:', companies); // Debug log
        setUserCompanies(companies);
      })
      .catch((err) => {
        console.error('Failed to fetch user data:', err); // Debug log
        // If fetching user data fails, set empty companies
        setUserCompanies([]);
      });
  }, [token]);


  const handleModalSave = async ({ name, timezone, companyId }) => {
    setModalLoading(true);
    setModalError(null);
    try {
      let cal;
      if (editCalendar) {
        cal = await CalendarsService.updateCalendar(editCalendar.id, { name, timezone, companyId }, token);
        setCalendars(calendars.map(c => c.id === cal.id ? cal : c));
      } else {
        cal = await CalendarsService.createCalendar({ name, timezone, companyId }, token);
        setCalendars([...calendars, cal]);
        setActiveCalendarId(cal.id);
      }
      setModalOpen(false);
      setEditCalendar(null);
    } catch (e) {
      setModalError("Opslaan mislukt.");
    }
    setModalLoading(false);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Verwijder deze kalender?")) return;
    setLoading(true);
    setError(null);
    try {
      await CalendarsService.deleteCalendar(id, token);
      setCalendars(calendars.filter((c) => c.id !== id));
      if (activeCalendarId === id && calendars.length > 1) {
        setActiveCalendarId(calendars.find((c) => c.id !== id).id);
      }
    } catch (e) {
      setError("Verwijderen mislukt.");
    }
    setLoading(false);
  };

  return (
    <aside className="p-5 w-full h-full overflow-y-auto bg-white dark:bg-gray-900 flex flex-col gap-6">
      <div className="md:hidden flex justify-between items-center">
        <h2 className="text-lg font-semibold text-gray-700 dark:text-gray-100">Menu</h2>
        <button
          type="button"
          onClick={onClose}
          className="btn-icon"
          aria-label="Sluiten"
        >
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="20" height="20" fill="currentColor" aria-hidden="true">
            <path d="M19 6.41 17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" />
          </svg>
        </button>
      </div>

      <div>
        <div className="flex items-center justify-between mb-2">
          <label className="section-label">Kalender</label>
          {!isReadonly && (
            <button
              type="button"
              className="text-xs text-blue-600 hover:underline disabled:text-gray-400"
              title="Nieuwe kalender"
              onClick={() => { setEditCalendar(null); setModalOpen(true); }}
              disabled={loading}
            >
              Nieuwe kalender
            </button>
          )}
        </div>
        {error && <div className="text-red-500 text-xs mb-1">{error}</div>}
        <div className="flex items-center gap-2 w-full">
          <select
            className="input-field flex-1 min-w-0 w-0 pr-8"
            value={activeCalendarId || ''}
            onChange={e => setActiveCalendarId(e.target.value)}
            disabled={loading || calendars.length === 0}
          >
            {calendars.map((cal) => (
              <option key={cal.id} value={cal.id}>{cal.name}</option>
            ))}
          </select>
          {!isReadonly && (
            <div className="flex items-center gap-0.5 flex-shrink-0 h-6">
              <button
                type="button"
                className="w-6 h-6 inline-flex items-center justify-center text-blue-600 hover:bg-blue-50 rounded disabled:opacity-40"
                title="Kalender bewerken"
                aria-label="Kalender bewerken"
                onClick={() => {
                  const cal = calendars.find(c => c.id === activeCalendarId);
                  if (cal) { setEditCalendar(cal); setModalOpen(true); }
                }}
                disabled={loading || !activeCalendarId}
              >
                <IconEdit />
              </button>
              <button
                type="button"
                className="w-6 h-6 inline-flex items-center justify-center text-red-600 hover:bg-red-50 dark:hover:bg-gray-800 rounded disabled:opacity-40"
                title="Kalender verwijderen"
                aria-label="Kalender verwijderen"
                onClick={() => handleDelete(activeCalendarId)}
                disabled={loading || !activeCalendarId}
              >
                <IconTrash />
              </button>
            </div>
          )}
        </div>
        <CalendarModal
          open={modalOpen}
          onClose={() => { setModalOpen(false); setEditCalendar(null); setModalError(null); }}
          onSave={handleModalSave}
          initial={editCalendar}
          loading={modalLoading}
          error={modalError}
          userCompanies={userCompanies}
        />
      </div>
      <CreateEventButton />
      <SmallCalendar />
      <Labels />
    </aside>
  );
}
